import { useCallback, useEffect, useMemo, useState } from "react";
import { apiUrl, authFetch, authHeaders } from "../config";
import styles from "./FbGroupPostsTab.module.css";

interface Site { id: string; name: string; domain: string; isDefault?: boolean }
interface GroupPost {
  id: number;
  recipe_name: string;
  source_input: string;
  source_type: string;
  group_caption: string;
  batch_id?: string | null;
  reference_image_url?: string | null;
  article_recipe_id?: number | null;
  article_image_url?: string | null;
  article_uploaded?: boolean;
  article_post_url?: string | null;
  article_site_id?: string | null;
  created_at?: string;
}
interface BulkItem { id: string; input: string; image: File | null; status: "idle" | "generating" | "done" | "error"; error?: string }

function newBulkItem(): BulkItem {
  return { id: crypto.randomUUID(), input: "", image: null, status: "idle" };
}

function firstComment(url?: string | null) {
  return url ? `Recipe | ${url}` : "Publish the article to create the first comment link.";
}

function articleImagePrompt(recipeName: string) {
  return `Create a highly realistic vertical 3:4 homemade recipe-process collage for ${recipeName}.

Use a real residential kitchen, dark speckled granite countertop, ordinary cookware, bright warm kitchen light, natural smartphone recipe photography, generous portions, and slightly imperfect homemade preparation. Create EXACTLY FOUR photographic panels separated by thin white lines: three smaller panels across the top showing authentic ingredients, hands performing the main preparation step, and the food ready to cook; then one large bottom panel showing the fully cooked dish in a close appetizing family-style serving. Keep the kitchen, cookware, ingredients, lighting, and food consistent in every panel. Show realistic moisture, browning, crumbs, sauce, melted cheese, or steam only when appropriate. No recipe title, captions, labels, measurements, instructions, watermark, logos, branded packaging, restaurant plating, perfect symmetry, plastic-looking food, malformed hands, duplicate utensils, or duplicate ingredients.`;
}

async function createArticleAndImage(post: GroupPost, siteId: string, useOriginalImage = false) {
  const form = new FormData();
  form.append("site_id", siteId);
  if (useOriginalImage) {
    form.append("use_reference_image", "1");
  } else {
  const imageResponse = await fetch("http://localhost:8787/generate", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ prompt: articleImagePrompt(post.recipe_name), size: "1200x1600", style: "photorealistic-natural" }),
  });
  const imageData = await imageResponse.json() as { url?: string; filename?: string; error?: string };
  if (!imageResponse.ok || !imageData.url) throw new Error(imageData.error || "Codex image generation failed");
  const generated = await fetch(`${imageData.url}?t=${Date.now()}`);
  if (!generated.ok) throw new Error("Could not read generated image");
  form.append("image", new File([await generated.blob()], imageData.filename || `${post.recipe_name}.png`, { type: "image/png" }));
  }
  const response = await authFetch(apiUrl(`/api/fb-group-posts/${post.id}/create-article`), { method: "POST", body: form });
  const data = await response.json() as { success?: boolean; recipeId?: number; imageUrl?: string; error?: string };
  if (!response.ok || !data.success || !data.recipeId) throw new Error(data.error || "Article creation failed");
  return { recipeId: data.recipeId, imageUrl: data.imageUrl || null };
}

function GroupPostPipelineCard({ post, sites, defaultSiteId, useOriginalImage, onUseOriginalImage, onDelete, onChanged, onNotice }: {
  post: GroupPost;
  sites: Site[];
  defaultSiteId: string;
  useOriginalImage: boolean;
  onUseOriginalImage: (value: boolean) => void;
  onDelete: () => void;
  onChanged: () => Promise<void>;
  onNotice: (notice: { kind: "ok" | "error"; text: string }) => void;
}) {
  const [siteId, setSiteId] = useState(post.article_site_id || defaultSiteId);
  const [caption, setCaption] = useState(post.group_caption);
  const [comment, setComment] = useState(firstComment(post.article_post_url));
  const [recipeId, setRecipeId] = useState<number | null>(post.article_recipe_id || null);
  const [imageUrl, setImageUrl] = useState<string | null>(post.article_image_url || null);
  const [postUrl, setPostUrl] = useState<string | null>(post.article_post_url || null);
  const [creating, setCreating] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [stage, setStage] = useState("");

  useEffect(() => { if (!siteId && defaultSiteId) setSiteId(defaultSiteId); }, [defaultSiteId, siteId]);
  useEffect(() => {
    setRecipeId(post.article_recipe_id || null);
    setImageUrl(post.article_image_url || null);
    setPostUrl(post.article_post_url || null);
    setComment(firstComment(post.article_post_url));
  }, [post.article_recipe_id, post.article_image_url, post.article_post_url]);

  const createArticle = async () => {
    if (!siteId || creating) return;
    setCreating(true); setStage(useOriginalImage ? "Using original post image…" : "Generating article image…");
    try {
      setStage("Writing article…");
      const data = await createArticleAndImage(post, siteId, useOriginalImage);
      setRecipeId(data.recipeId); setImageUrl(data.imageUrl);
      onNotice({ kind: "ok", text: `${post.recipe_name}: article and image created.` });
      await onChanged();
    } catch (error) {
      onNotice({ kind: "error", text: error instanceof Error ? error.message : "Article creation failed" });
    } finally { setCreating(false); setStage(""); }
  };

  const publish = async () => {
    if (!recipeId || !siteId || publishing) return;
    setPublishing(true);
    try {
      const response = await authFetch(apiUrl(`/api/recipes/${recipeId}/publish`), { method: "POST", headers: authHeaders(), body: JSON.stringify({ siteId }) });
      const data = await response.json() as { success?: boolean; post_url?: string; error?: string };
      if (!response.ok || !data.success || !data.post_url) throw new Error(data.error || "Website publishing failed");
      setPostUrl(data.post_url); setComment(firstComment(data.post_url));
      onNotice({ kind: "ok", text: `${post.recipe_name}: published to website.` });
      await onChanged();
    } catch (error) {
      onNotice({ kind: "error", text: error instanceof Error ? error.message : "Website publishing failed" });
    } finally { setPublishing(false); }
  };

  return <article className={styles.bulkCard}>
    <div className={styles.pipelineHeader}><div><span>GROUP POST</span><h2>{post.recipe_name}</h2></div><div className={styles.pipelineHeaderActions}><b>{postUrl ? "Published ✓" : recipeId ? "Article ready" : "Caption ready"}</b><button onClick={onDelete}>Delete</button></div></div>
    <div className={styles.contentGrid}>
      <div className={styles.mediaPanel}>
        <div className={styles.images}>
          {post.reference_image_url && <figure><img src={post.reference_image_url} alt="Original Facebook post" /><figcaption>Original post</figcaption></figure>}
          {imageUrl && <figure><img src={imageUrl} alt="Generated article" /><figcaption>Article image</figcaption></figure>}
          {!post.reference_image_url && !imageUrl && <div className={styles.emptyImage}>Article image will appear here</div>}
        </div>
        <label>Publishing website<select value={siteId} onChange={event => setSiteId(event.target.value)}><option value="">Select website…</option>{sites.map(site => <option key={site.id} value={site.id}>{site.name} — {site.domain}</option>)}</select></label>
        {post.reference_image_url && !recipeId && <label className={styles.originalImageOption}><input type="checkbox" checked={useOriginalImage} onChange={event => onUseOriginalImage(event.target.checked)} /><span><b>Use original post image</b><small>Skip AI image generation for this article.</small></span></label>}
        {stage && <p className={styles.stage}>{stage}</p>}
        {!recipeId ? <button className={styles.articleButton} onClick={createArticle} disabled={!siteId || creating}>{creating ? "Creating article…" : "Create article + image"}</button>
          : postUrl ? <a className={styles.articleButton} href={postUrl} target="_blank" rel="noreferrer">Open published article ↗</a>
            : <button className={styles.articleButton} onClick={publish} disabled={!siteId || publishing}>{publishing ? "Publishing…" : "Publish article to website"}</button>}
      </div>
      <div className={styles.copyPanel}>
        <label><span>Facebook group caption <button onClick={() => navigator.clipboard.writeText(caption)}>Copy</button></span><textarea value={caption} onChange={event => setCaption(event.target.value)} /></label>
        <label><span>First comment <button disabled={!postUrl} onClick={() => navigator.clipboard.writeText(comment)}>Copy</button></span><textarea value={comment} onChange={event => setComment(event.target.value)} /></label>
      </div>
    </div>
  </article>;
}

export function FbGroupPostsTab() {
  const [input, setInput] = useState("");
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkItems, setBulkItems] = useState<BulkItem[]>(() => [newBulkItem(), newBulkItem()]);
  const [batchResults, setBatchResults] = useState<GroupPost[]>([]);
  const [batchProgress, setBatchProgress] = useState<{ done: number; total: number; failed: number } | null>(null);
  const [referenceImage, setReferenceImage] = useState<File | null>(null);
  const [posts, setPosts] = useState<GroupPost[]>([]);
  const [current, setCurrent] = useState<GroupPost | null>(null);
  const [sites, setSites] = useState<Site[]>([]);
  const [siteId, setSiteId] = useState("");
  const [caption, setCaption] = useState("");
  const [comment, setComment] = useState("");
  const [generating, setGenerating] = useState(false);
  const [articleGenerating, setArticleGenerating] = useState(false);
  const [bulkArticleGenerating, setBulkArticleGenerating] = useState(false);
  const [bulkArticleProgress, setBulkArticleProgress] = useState<{ done: number; total: number; failed: number } | null>(null);
  const [originalImageChoices, setOriginalImageChoices] = useState<Record<number, boolean>>({});
  const [useOriginalSingle, setUseOriginalSingle] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [stage, setStage] = useState("");
  const [notice, setNotice] = useState<{ kind: "ok" | "error"; text: string } | null>(null);

  const recentBatches = useMemo(() => {
    const groups = new Map<string, GroupPost[]>();
    posts.forEach(post => {
      if (!post.batch_id) return;
      const group = groups.get(post.batch_id) || [];
      group.push(post);
      groups.set(post.batch_id, group);
    });
    return [...groups.entries()]
      .map(([id, batchPosts]) => ({ id, posts: batchPosts, createdAt: batchPosts[0]?.created_at || "" }))
      .filter(batch => batch.posts.length >= 2)
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }, [posts]);
  const hydratedBatchResults = useMemo(() => batchResults.map(item => {
    const saved = posts.find(post => post.id === item.id);
    return saved ? { ...item, ...saved } : item;
  }), [batchResults, posts]);

  const preview = useMemo(() => referenceImage ? URL.createObjectURL(referenceImage) : "", [referenceImage]);
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

  const load = useCallback(async () => {
    const [postsResponse, sitesResponse] = await Promise.all([
      authFetch(apiUrl("/api/fb-group-posts")),
      authFetch(apiUrl("/api/sites")),
    ]);
    if (postsResponse.ok) {
      const data = await postsResponse.json() as { posts?: GroupPost[] };
      setPosts(data.posts || []);
    }
    if (sitesResponse.ok) {
      const data = await sitesResponse.json() as { sites?: Site[] };
      const available = data.sites || [];
      setSites(available);
      const defaultSite = available.find(site => site.isDefault) || available[0];
      if (defaultSite) setSiteId(value => value || defaultSite.id);
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const selectPost = (post: GroupPost) => {
    setBulkMode(false);
    setCurrent(post);
    setCaption(post.group_caption);
    setComment(firstComment(post.article_post_url));
    if (post.article_site_id) setSiteId(post.article_site_id);
    setNotice(null);
    setUseOriginalSingle(false);
  };

  const generateOne = async (sourceInput: string, image?: File | null, batchId?: string) => {
    const form = new FormData();
    form.append("input", sourceInput.trim());
    if (batchId) form.append("batch_id", batchId);
    if (image) form.append("image", image);
    const response = await authFetch(apiUrl("/api/fb-group-posts/generate"), { method: "POST", body: form });
    const data = await response.json() as { post?: GroupPost; error?: string };
    if (!response.ok || !data.post) throw new Error(data.error || "Group post creation failed");
    return data.post;
  };

  const generate = async () => {
    if (generating) return;
    if (!bulkMode && !input.trim()) return;
    setGenerating(true); setNotice(null); setBatchProgress(null);
    try {
      if (!bulkMode) {
        const post = await generateOne(input, referenceImage);
        setCurrent(post); setCaption(post.group_caption); setComment(firstComment());
        setUseOriginalSingle(false);
        setInput(""); setReferenceImage(null);
        setNotice({ kind: "ok", text: `Created the Facebook group caption for “${post.recipe_name}”.` });
      } else {
        const items = bulkItems.filter(item => item.input.trim());
        if (items.length < 2) throw new Error("Add at least two Facebook group posts.");
        if (items.length > 30) throw new Error("A batch can contain up to 30 posts.");
        setBatchResults([]);
        setBatchProgress({ done: 0, total: items.length, failed: 0 });
        setBulkItems(currentItems => currentItems.map(item => items.some(active => active.id === item.id) ? { ...item, status: "idle", error: undefined } : item));
        let nextIndex = 0; let done = 0; let failed = 0;
        const batchId = crypto.randomUUID();
        const created: GroupPost[] = [];
        const worker = async () => {
          while (nextIndex < items.length) {
            const item = items[nextIndex++];
            if (!item) break;
            setBulkItems(currentItems => currentItems.map(candidate => candidate.id === item.id ? { ...candidate, status: "generating" } : candidate));
            try {
              const post = await generateOne(item.input, item.image, batchId);
              created.push(post);
              setBulkItems(currentItems => currentItems.map(candidate => candidate.id === item.id ? { ...candidate, status: "done" } : candidate));
            } catch (error) {
              failed += 1;
              const message = error instanceof Error ? error.message : "Failed";
              setBulkItems(currentItems => currentItems.map(candidate => candidate.id === item.id ? { ...candidate, status: "error", error: message } : candidate));
            } finally {
              done += 1; setBatchProgress({ done, total: items.length, failed });
            }
          }
        };
        await Promise.all(Array.from({ length: Math.min(2, items.length) }, () => worker()));
        setBatchResults(created);
        if (created[0]) setCurrent(created[0]);
        setNotice({ kind: failed ? "error" : "ok", text: `Created ${created.length} of ${items.length} Facebook group posts.${failed ? ` ${failed} failed.` : ""}` });
      }
      await load();
    } catch (error) {
      setNotice({ kind: "error", text: error instanceof Error ? error.message : "Group post creation failed" });
    } finally { setGenerating(false); }
  };

  const createArticle = async () => {
    if (!current || !siteId || articleGenerating) return;
    setArticleGenerating(true); setStage(useOriginalSingle ? "Using original post image…" : "Generating homemade article image…"); setNotice(null);
    try {
      setStage("Writing recipe JSON and article…");
      const data = await createArticleAndImage(current, siteId, useOriginalSingle);
      const updated = { ...current, article_recipe_id: data.recipeId, article_image_url: data.imageUrl, article_site_id: siteId };
      setCurrent(updated);
      setNotice({ kind: "ok", text: "Recipe JSON, full article, and article image created." });
      await load();
    } catch (error) {
      const text = error instanceof Error ? error.message : "Article creation failed";
      setNotice({ kind: "error", text: text.includes("fetch") ? "Start the local bridge with python scripts/image_gen_server.py, then try again." : text });
    } finally { setArticleGenerating(false); setStage(""); }
  };

  const publish = async () => {
    if (!current?.article_recipe_id || !siteId || publishing) return;
    setPublishing(true); setNotice(null);
    try {
      const response = await authFetch(apiUrl(`/api/recipes/${current.article_recipe_id}/publish`), {
        method: "POST", headers: authHeaders(), body: JSON.stringify({ siteId }),
      });
      const data = await response.json() as { success?: boolean; post_url?: string; error?: string };
      if (!response.ok || !data.success || !data.post_url) throw new Error(data.error || "Website publishing failed");
      const updated = { ...current, article_uploaded: true, article_post_url: data.post_url, article_site_id: siteId };
      setCurrent(updated); setComment(firstComment(data.post_url));
      setNotice({ kind: "ok", text: `Article published to ${sites.find(site => site.id === siteId)?.name || "your website"}.` });
      await load();
    } catch (error) {
      setNotice({ kind: "error", text: error instanceof Error ? error.message : "Website publishing failed" });
    } finally { setPublishing(false); }
  };

  const updateBulkItem = (id: string, changes: Partial<BulkItem>) => {
    setBulkItems(items => items.map(item => item.id === id ? { ...item, ...changes } : item));
  };

  const createAllArticles = async () => {
    if (!siteId || bulkArticleGenerating) return;
    const targets = hydratedBatchResults.filter(post => !post.article_recipe_id);
    if (!targets.length) {
      setNotice({ kind: "ok", text: "Every article and image in this bulk operation is already created." });
      return;
    }
    setBulkArticleGenerating(true);
    setBulkArticleProgress({ done: 0, total: targets.length, failed: 0 });
    setNotice(null);
    let nextIndex = 0; let done = 0; let failed = 0;
    const worker = async () => {
      while (nextIndex < targets.length) {
        const post = targets[nextIndex++];
        if (!post) break;
        try {
          const data = await createArticleAndImage(post, siteId, Boolean(originalImageChoices[post.id] && post.reference_image_url));
          setBatchResults(items => items.map(item => item.id === post.id ? {
            ...item,
            article_recipe_id: data.recipeId,
            article_image_url: data.imageUrl,
            article_site_id: siteId,
          } : item));
        } catch (error) {
          failed += 1;
          console.error(`Failed to create article for ${post.recipe_name}`, error);
        } finally {
          done += 1;
          setBulkArticleProgress({ done, total: targets.length, failed });
        }
      }
    };
    await Promise.all(Array.from({ length: Math.min(3, targets.length) }, () => worker()));
    await load();
    setBulkArticleGenerating(false);
    setNotice({
      kind: failed ? "error" : "ok",
      text: `Created ${targets.length - failed} of ${targets.length} articles and images.${failed ? ` ${failed} failed and can be retried.` : ""}`,
    });
  };

  const deletePost = async (post: GroupPost) => {
    if (!window.confirm(`Delete “${post.recipe_name}” from this bulk operation?`)) return;
    try {
      const response = await authFetch(apiUrl(`/api/fb-group-posts/${post.id}`), { method: "DELETE" });
      const data = await response.json() as { success?: boolean; error?: string };
      if (!response.ok || !data.success) throw new Error(data.error || "Delete failed");
      setBatchResults(items => items.filter(item => item.id !== post.id));
      if (current?.id === post.id) setCurrent(null);
      setNotice({ kind: "ok", text: `Deleted “${post.recipe_name}”.` });
      await load();
    } catch (error) {
      setNotice({ kind: "error", text: error instanceof Error ? error.message : "Delete failed" });
    }
  };

  return <div className={styles.page}>
    {notice && <div className={`${styles.notice} ${styles[notice.kind]}`}>{notice.text}</div>}
    <div className={styles.layout}>
      <section className={styles.composer}>
        <div className={styles.heading}><div><span>FACEBOOK GROUPS</span><h1>Create recipe group post</h1></div><small>No video or Reel script</small></div>
        <div className={styles.modeSwitch}><button className={!bulkMode ? styles.active : ""} onClick={() => setBulkMode(false)}>Single</button><button className={bulkMode ? styles.active : ""} onClick={() => { setBulkMode(true); setReferenceImage(null); }}>Bulk creation</button><span>Two posts generated at a time</span></div>
        {!bulkMode && <textarea value={input} onChange={event => setInput(event.target.value)} placeholder="Recipe name, full recipe, or an existing Facebook caption" />}
        {!bulkMode && <div className={styles.reference}>
          {preview ? <img src={preview} alt="Recipe reference" /> : <div className={styles.imagePlaceholder}>＋</div>}
          <div><strong>Original post image <em>Optional</em></strong><small>Use a scraped thumbnail or upload the Facebook post image.</small></div>
          <label><input type="file" accept="image/*" onChange={event => setReferenceImage(event.target.files?.[0] || null)} />{referenceImage ? "Replace" : "Choose image"}</label>
          {referenceImage && <button onClick={() => setReferenceImage(null)}>Remove</button>}
        </div>}
        {bulkMode && <div className={styles.bulkItems}>{bulkItems.map((item, index) => <div className={`${styles.bulkItem} ${item.status === "done" ? styles.bulkDone : item.status === "error" ? styles.bulkError : ""}`} key={item.id}>
          <div className={styles.bulkHeader}><strong>POST {String(index + 1).padStart(2, "0")}</strong><span>{item.status === "generating" ? "Generating…" : item.status === "done" ? "Created ✓" : item.status === "error" ? "Failed" : "Ready"}</span>{bulkItems.length > 2 && <button onClick={() => setBulkItems(items => items.filter(candidate => candidate.id !== item.id))}>Remove</button>}</div>
          <textarea value={item.input} onChange={event => updateBulkItem(item.id, { input: event.target.value, status: "idle", error: undefined })} placeholder="Recipe name, full recipe, or Facebook caption" />
          <div className={styles.bulkImage}><div>{item.image ? "✓" : "＋"}</div><span>{item.image ? item.image.name : "Original post image (optional)"}</span><label><input type="file" accept="image/*" onChange={event => updateBulkItem(item.id, { image: event.target.files?.[0] || null })} />{item.image ? "Replace" : "Choose"}</label>{item.image && <button onClick={() => updateBulkItem(item.id, { image: null })}>Clear</button>}</div>
          {item.error && <small>{item.error}</small>}
        </div>)}<button className={styles.addBulk} disabled={bulkItems.length >= 30 || generating} onClick={() => setBulkItems(items => [...items, newBulkItem()])}>＋ Add another post</button></div>}
        {batchProgress && generating && <div className={styles.progress}><div style={{ width: `${Math.round(batchProgress.done / batchProgress.total * 100)}%` }} /><span>{batchProgress.done}/{batchProgress.total}{batchProgress.failed ? ` · ${batchProgress.failed} failed` : ""}</span></div>}
        <button className={styles.generate} onClick={generate} disabled={generating || (bulkMode ? bulkItems.filter(item => item.input.trim()).length < 2 : !input.trim())}>{generating ? (bulkMode ? `Creating ${batchProgress?.done || 0}/${batchProgress?.total || "…"}` : "Detecting recipe…") : bulkMode ? "Create all group posts →" : "Create group post →"}</button>
      </section>

      <aside className={styles.history}>
        {recentBatches.length > 0 && <><div className={styles.heading}><div><span>RECENT</span><h2>Bulk operations</h2></div></div>
        <div className={styles.historyList}>{recentBatches.map((batch, index) => {
          const completed = batch.posts.every(post => post.article_uploaded);
          return <button key={batch.id} className={completed ? styles.completedBatch : ""} onClick={() => { setBulkMode(true); setBatchResults(batch.posts); setCurrent(batch.posts[0] || null); setNotice(null); }}>
            <strong>Bulk {String(index + 1).padStart(2, "0")}</strong><span>{completed ? "Published ✓" : `${batch.posts.length} group posts`}</span>
          </button>;
        })}</div><div className={styles.historyDivider} /></>}
        <div className={styles.heading}><div><span>RECENT</span><h2>Group posts</h2></div></div>
        <div className={styles.historyList}>{posts.map(post => <button key={post.id} className={current?.id === post.id ? styles.selected : ""} onClick={() => selectPost(post)}><strong>{post.recipe_name}</strong><span>{post.article_uploaded ? "Published ✓" : post.article_recipe_id ? "Article ready" : "Caption ready"}</span></button>)}{!posts.length && <p>No group posts created yet.</p>}</div>
      </aside>
    </div>

    {bulkMode && batchResults.length > 0 && <>
      <section className={styles.bulkArticleToolbar}>
        <div><strong>Create every article + image</strong><small>Runs up to three Codex image and article jobs in parallel.</small></div>
        <select value={siteId} onChange={event => setSiteId(event.target.value)}><option value="">Select website…</option>{sites.map(site => <option key={site.id} value={site.id}>{site.name} — {site.domain}</option>)}</select>
        <button onClick={createAllArticles} disabled={!siteId || bulkArticleGenerating || hydratedBatchResults.every(post => post.article_recipe_id)}>{bulkArticleGenerating ? `Creating ${bulkArticleProgress?.done || 0}/${bulkArticleProgress?.total || hydratedBatchResults.length}…` : "Create all articles + images"}</button>
        {bulkArticleProgress && <div className={styles.bulkArticleProgress}><i style={{ width: `${Math.round((bulkArticleProgress.done / bulkArticleProgress.total) * 100)}%` }} /><span>{bulkArticleProgress.done}/{bulkArticleProgress.total}{bulkArticleProgress.failed ? ` · ${bulkArticleProgress.failed} failed` : ""}</span></div>}
      </section>
      <section className={styles.bulkResults}>{hydratedBatchResults.map(post => <GroupPostPipelineCard key={post.id} post={post} sites={sites} defaultSiteId={siteId} useOriginalImage={Boolean(originalImageChoices[post.id])} onUseOriginalImage={value => setOriginalImageChoices(choices => ({ ...choices, [post.id]: value }))} onDelete={() => void deletePost(post)} onChanged={load} onNotice={setNotice} />)}</section>
    </>}

    {!bulkMode && current && <section className={styles.pipeline}>
      <div className={styles.pipelineHeader}><div><span>GROUP POST PIPELINE</span><h2>{current.recipe_name}</h2></div><div className={styles.pipelineHeaderActions}><b>{current.article_uploaded ? "Published ✓" : current.article_recipe_id ? "Article ready" : "Caption ready"}</b><button onClick={() => void deletePost(current)}>Delete</button></div></div>
      <div className={styles.contentGrid}>
        <div className={styles.mediaPanel}>
          <div className={styles.images}>
            {current.reference_image_url && <figure><img src={current.reference_image_url} alt="Original Facebook post" /><figcaption>Original post</figcaption></figure>}
            {current.article_image_url && <figure><img src={current.article_image_url} alt="Generated article" /><figcaption>Article image</figcaption></figure>}
            {!current.reference_image_url && !current.article_image_url && <div className={styles.emptyImage}>Article image will appear here</div>}
          </div>
          <label>Publishing website<select value={siteId} onChange={event => setSiteId(event.target.value)}><option value="">Select website…</option>{sites.map(site => <option key={site.id} value={site.id}>{site.name} — {site.domain}</option>)}</select></label>
          {current.reference_image_url && !current.article_recipe_id && <label className={styles.originalImageOption}><input type="checkbox" checked={useOriginalSingle} onChange={event => setUseOriginalSingle(event.target.checked)} /><span><b>Use original post image</b><small>Skip AI image generation for this article.</small></span></label>}
          {stage && <p className={styles.stage}>{stage}</p>}
          {!current.article_recipe_id
            ? <button className={styles.articleButton} onClick={createArticle} disabled={!siteId || articleGenerating}>{articleGenerating ? "Creating article…" : "Create article + image"}</button>
            : current.article_uploaded
              ? current.article_post_url && <a className={styles.articleButton} href={current.article_post_url} target="_blank" rel="noreferrer">Open published article ↗</a>
              : <button className={styles.articleButton} onClick={publish} disabled={!siteId || publishing}>{publishing ? "Publishing…" : "Publish article to website"}</button>}
        </div>
        <div className={styles.copyPanel}>
          <label><span>Facebook group caption <button onClick={() => navigator.clipboard.writeText(caption)}>Copy</button></span><textarea value={caption} onChange={event => setCaption(event.target.value)} /></label>
          <label><span>First comment <button disabled={!current.article_post_url} onClick={() => navigator.clipboard.writeText(comment)}>Copy</button></span><textarea value={comment} onChange={event => setComment(event.target.value)} /></label>
          <p>Post the image and group caption manually, then add the first comment after the article is published.</p>
        </div>
      </div>
    </section>}
  </div>;
}
